import { Inject, Injectable } from "@angular/core";
import { AppMetaData, AppMetaDataItemNames, CourseAvailabilityNotificationEntity, CourseEntity, CourseLessonEntity, CourseLessonProgressEntity, CourseLessonSessionEntity, CourseModuleProgressEntity, LessonListForStudentQueryEntity, LessonTypeAllItems, ReferenceStatusAllItems, RoAssignmentStudentCourseLessonProgressEntity, StudentCourseEntity, StudentCourseProgressByStudentCourseQueryEntity, StudentCourseProgressQueryEntity } from "../../../meta-data/app-meta-data.service";
import { DevwareMetaData, DwCacheService, DwConfigService, DwConfigServiceToken, DwEventService, DwMediaRepositoryService, DwMetaDataService, DwMetaDataServiceToken, DwModalButtonStandardConfigs, DwModalService, DwOrmData, DwOrmDataService, DwOrmDataServiceToken, DwQueryHelper, DwSecurityTopics, DwSecurityUserService, areEqual, deepCopy, limitLength } from "@devwareapps/devware-cap";
import { Observable, combineLatest, of } from "rxjs";
import { Router } from "@angular/router";
import { catchError, map, mergeMap, tap } from "rxjs/operators";
import { LessonReferenceInfo } from "../models/lesson-reference-info.model";
import { LessonProgressHierarchy } from "../models/lesson-progress-hierarchy.model";
import { CourseProgressItem, LessonProgressItem, ModuleProgressItem } from "../models/course-progress-hierarchy.model";
import { DateTimeUtilService } from "../../shared/util/date-time-util.service";
import { HttpClient } from "@angular/common/http";
import { CourseTotalTimeUpdateResult } from "../models/course-total-time-update-result.model";
import { StudentUpdateLessonProgressRequest } from "../models/student-update-lesson-progress-request.model";
import { StudentUpdateLessonProgressResult } from "../models/student-update-lesson-progress-result.model";
import { EndorsementCreationResult } from "../models/endorsement-creation-result.model";
import { AdminManagerService } from "../../admin/services/admin-manager.service";
import { GamificationRepositoryService } from "../../gamification/services/gamification-repository.service";

export interface CourseSummary {
    courseId: number;
    courseName: string;
    moduleCount: number;
    lessonCount: number;
}

@Injectable({ providedIn: 'root' })
export class CourseRepositoryService {
    
    public static readonly COURSE_PROGRESS_UPDATE_EVENT = 'CourseProgressUpdateEvent';
    courseApi: string;
    studentProgressApi: string;
    endorsementsApi: string;

    constructor(@Inject(DwOrmDataServiceToken) private dwOrmDataService: DwOrmDataService,
        @Inject(DwMetaDataServiceToken) private dwMetaDataService: DwMetaDataService,
        @Inject(DwConfigServiceToken) configService: DwConfigService,
        private dwSecurityUserService: DwSecurityUserService,
        private dwCacheService: DwCacheService,
        private dwEventService: DwEventService,
        private dateTimeUtilService: DateTimeUtilService,
        private dwMediaRepository: DwMediaRepositoryService,
        private router: Router,
        private dwModalService: DwModalService,
        private http: HttpClient,
        private adminManagerService: AdminManagerService,
        private gamificationRepositoryService: GamificationRepositoryService,
    ) { 
        this.studentProgressApi = `${configService.coreConfig.apiRoot}/aviator-online/students/progress`;
        this.courseApi = `${configService.coreConfig.apiRoot}/aviator-online/courses`;
        this.endorsementsApi = `${configService.coreConfig.apiRoot}/aviator-online/endorsements`;
    }

    updateCourseTotalTime(courseId: number): Observable<CourseTotalTimeUpdateResult> {
        const url = `${this.courseApi}/update-course-total-time?courseId=${courseId}`;

        return this.http.get<CourseTotalTimeUpdateResult>(url)
            .pipe(map(result => {
                if (result.IsSuccess && result.ChangesMade) {
                    // We need to trigger that student was updated so related grids are updated (Course progress grid is based on student and not student course)
                    this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.Course.ItemDetail.itemName), {});
                    this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaData.CourseModule.ItemDetail.itemName), {});
                }

                return result;
            }))
    }

    copyCourse(courseId: number): Observable<CourseEntity> {
        return this.loadCourseForCourse(courseId)
            .pipe(mergeMap(course => {
                course.CourseId = null;
                course.CourseName = `${course.CourseName} - Copy`;
                const copyKeyText = '-copy';
                course.CourseKey = limitLength(course.CourseName, 20 - copyKeyText.length) + copyKeyText;

                course.DefaultSchoolCourse = false;
                course.CreatedBy = null;
                course.CreatedDateTime = null;
                

                for (const module of course.CourseModule) { 
                    module.CourseId = null;
                    module.CourseModuleId = null;
                    module.CreatedBy = null;
                    module.CreatedDateTime = null;
                

                    for (const lesson of module.CourseLesson) {
                        lesson.CourseLessonId = null;
                        lesson.CourseModuleId = null;
                        lesson.CreatedBy = null;
                        lesson.CreatedDateTime = null;
                    }
                }

                return this.dwOrmDataService.saveEntity(course, true);
            }));
    }

    recreateEndorsements(courseId: number): Observable<EndorsementCreationResult> {
        const url = `${this.endorsementsApi}/recreate-endorsements?courseId=${courseId}`;

        return this.http.get<EndorsementCreationResult>(url);
    }

    getLessonImageUrl(lessonProgress: LessonListForStudentQueryEntity, width: number = 250, height: number = 141): { mainImageUrl: string, backupImageUrl: string } {
        const imageUrls: string[] = [];

        if (lessonProgress.LessonImageDwroMediaMediaPath) {
            imageUrls.push(this.dwMediaRepository.getImageUrlFromMediaPath(lessonProgress.LessonImageDwroMediaMediaPath, width, height));
        } else {
            if (lessonProgress.VideoThumbnailUrl) {
                imageUrls.push(lessonProgress.VideoThumbnailUrl);
            }
        }

        if (lessonProgress.ModuleImageMediaPath) {
            imageUrls.push(this.dwMediaRepository.getImageUrlFromMediaPath(lessonProgress.ModuleImageMediaPath, width, height));
        }
        if (lessonProgress.CourseImageMediaPath) {
            imageUrls.push(this.dwMediaRepository.getImageUrlFromMediaPath(lessonProgress.CourseImageMediaPath, width, height));
        }

        imageUrls.push('/assets/aviator-online/img/lesson-image-default.png');

        return { mainImageUrl: imageUrls[0], backupImageUrl: imageUrls[1] };
    }

    getCourse(courseId: number): Observable<CourseEntity> {
        const query = AppMetaData.Course.CreateQueryBuilder();

        query.setFieldSettings(s => s.LoadAllLookupDisplayFields = true);
        query.addFilterAnd(f => f.Equal(a => a.CourseId, courseId));
        query.addPrefetch(r => r.DwRoMediaByCourseImageMediaId);

        return this.dwOrmDataService.executeQuerySingle(query);
    }

    getCourseHierarchy(courseId: number): Observable<CourseEntity> {
        const cacheInvalidationTopics: string[] = [];

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.Course));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.CourseModule));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.CourseLesson));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.Video));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.Quiz));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.CourseLessonDocumentLink));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.CourseLessonDocumentLinkReference));

        const courseKey = `Course_${courseId}_LessonDocumentReferences`;

        // The installed CAP cache implementation takes this value in minutes.
        return this.dwCacheService.get(courseKey, this.loadCourseHierarchy(courseId), 10, cacheInvalidationTopics);
    }

    getCourseSummaries(): Observable<CourseSummary[]> {
        const cacheInvalidationTopics: string[] = [];

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.Course));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.CourseModule));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.CourseLesson));

        return this.dwCacheService.get('CourseSummaries', this.loadCourseSummaries(), null, cacheInvalidationTopics);
    }

    getCourseSummary(courseId: number): Observable<CourseSummary> {
        return this.getCourseSummaries()
            .pipe(map(courseSummaries => (courseSummaries || []).find(courseSummary => courseSummary.courseId == courseId) || null));
    }

    getCurrentStudentCourses(): Observable<StudentCourseEntity[]> {
        const cacheInvalidationTopics: string[] = [DwSecurityTopics.SECURITY_CHANGED];

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.StudentCourse));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.CourseEndorsementStudent));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.AcsWrittenResult));
        cacheInvalidationTopics.push( CourseRepositoryService.COURSE_PROGRESS_UPDATE_EVENT);

        const courseKey = `StudentCourses`;

        return this.dwCacheService.get(courseKey, this.loadCurrentStudentCourses(), null, cacheInvalidationTopics);
    }

    getStudentCourse(courseId: number): Observable<StudentCourseEntity> {
        const cacheInvalidationTopics: string[] = [DwSecurityTopics.SECURITY_CHANGED];

        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.StudentCourse));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.CourseModuleProgress));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.CourseLessonProgress));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.CourseEndorsementStudent));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.AcsWrittenResult));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.Video));
        cacheInvalidationTopics.push(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.Quiz));

        const courseKey = `StudentCourse_${courseId}`;

        return this.dwCacheService.get(courseKey, this.loadStudentCourseWithProgressEnsured(courseId), null, cacheInvalidationTopics);
    }


    getStudentCourseProgressQuery(StudentCourseId: number): Observable<StudentCourseProgressQueryEntity> {
        return AppMetaData.Queries.StudentCourseProgress.CreateQueryBuilder(this.dwMetaDataService)
            .pipe(mergeMap(query => {

                const qh = new DwQueryHelper();

                // clone query so we don't modify the original (//TODO: Need to fix this in the framework - thought it was resolved, but it came back here)
                query.query = qh.cloneQuery(query.query);

                query.addFilterAnd(f => f.Equal(a => a.StudentCourseStudentCourseId, StudentCourseId));

                return this.dwOrmDataService.executeQuerySingle(query);
            }));
    }

    getStudentCourseProgressByStudentCourse(StudentCourseId: number): Observable<StudentCourseProgressByStudentCourseQueryEntity> {
        return AppMetaData.Queries.StudentCourseProgressByStudentCourse.CreateQueryBuilder(this.dwMetaDataService)
            .pipe(mergeMap(query => {

                return AppMetaData.Queries.StudentModuleProgress.CreateQueryBuilder(this.dwMetaDataService)
                    .pipe(mergeMap(moduleQuery => {
                        const qh = new DwQueryHelper();

                        const clonedQuery = qh.cloneQuery(query.query);

                        query = AppMetaData.Queries.StudentCourseProgressByStudentCourse.CreateQueryBuilderFromQuery(clonedQuery);

                        //console.log(`StudentgetStudentCourseProgressByStudentCourse  Query Count = ${(query as any).count}`, query);

                        moduleQuery.addOrderBy(o => o.CourseModuleModuleOrder);

                        query.addFilterAnd(f => f.Equal(a => a.StudentCourseId, StudentCourseId));

                        query.addPrefetch(r => r.CourseModuleProgress, moduleQuery.query);

                        return this.dwOrmDataService.executeQuerySingle(query);
                    }));
            }));
    }

    updateStudentCourseCache(studentCourse: StudentCourseEntity, publishEvent: boolean = true) {

        if (!studentCourse) {
            return;
        }

        const cacheInvalidationTopics: string[] = [DwSecurityTopics.SECURITY_CHANGED];
        const courseKey = `StudentCourse_${studentCourse.CourseId}`;

        this.dwCacheService.set(courseKey, studentCourse, null, cacheInvalidationTopics);

        if (publishEvent) {
            this.publishCourseProgressUpdate(studentCourse);
        }
    }

    public publishCourseProgressUpdate(studentCourse: StudentCourseEntity) {
        this.dwEventService.publishEvent(CourseRepositoryService.COURSE_PROGRESS_UPDATE_EVENT, studentCourse);
    }

    public getCourseProgressUpdate(): Observable<StudentCourseEntity> {
        return this.dwEventService.getEventData(CourseRepositoryService.COURSE_PROGRESS_UPDATE_EVENT);
    }

    public publishReloadStudentCourse(studentCourse: StudentCourseEntity) {
        this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.StudentCourse), studentCourse);
        this.dwEventService.publishEvent(DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.Course), {});

        this.publishCourseProgressUpdate(studentCourse);
    }

    getCourseProgress(courseId: number, studendCourse?: StudentCourseEntity): Observable<CourseProgressItem> {
        const obs: Observable<any>[] = [];

        obs.push(this.getCourseHierarchy(courseId));

        const studentCourseOb = studendCourse ? of(studendCourse) : this.getStudentCourse(courseId);

        obs.push(studentCourseOb);

        return combineLatest(obs)
            .pipe(map(([course, studentCourse]) => this.buildCourseProgressItems(course, studentCourse)));
    }

    private buildCourseProgressItems(course: CourseEntity, studentCourse: StudentCourseEntity): CourseProgressItem {
        if (!course || !studentCourse) {
            return null;
        }

        const courseNavItem: CourseProgressItem = {
            course: course || {} as any,
            studentCourse: studentCourse,
            modules: [],
            totalLessons: 0,
            completedLessons: 0,
            percentComplete: 0
        };

        for (const module of course.CourseModule) {

            const moduleProgress = studentCourse.CourseModuleProgress.find(m => m.CourseModuleId == module.CourseModuleId);
            // The StudentModuleSummary relationship is many-to-one in shape but
            // only a single active record is expected per CourseModuleProgress.
            // Pick the first non-empty body so the consumer can do a simple
            // truthiness check.
            //const summaryHtml = moduleProgress?.AIPromptRequest?.find(s => !!s?.ModuleSummaryHtml)?.ModuleSummaryHtml;

            const summaryHtml = moduleProgress?.AIPromptRequest?.Result;
            
            const moduleNav: ModuleProgressItem = {
                module: module,
                moduleProgress: moduleProgress,
                lessons: [],
                totalLessons: 0,
                completedLessons: 0,
                aiSummaryHtml: summaryHtml || undefined
            };

            let previousLessonComplete = false;
            for (const lesson of module.CourseLesson) {
                const lessonProgress = moduleNav.moduleProgress?.CourseLessonProgress.find(m => m.CourseLessonId == lesson.CourseLessonId);
                const lessonNav: LessonProgressItem = {
                    lesson: lesson,
                    lessonProgress: lessonProgress,
                    percentComplete: lessonProgress?.PercentComplete || 0,
                    isComplete: lessonProgress?.LessonComplete || false,
                    lessonIconClass: this.determineLessonIcon(lesson, lessonProgress),
                    durationSeconds: lesson.Video?.VideoDurationSeconds,
                    totalQuestions: lesson.Quiz?.MaxQuestionsToShow
                };

                // Set the furtherest lesson
                if (previousLessonComplete) {
                    courseNavItem.furtherestLesson = lesson;
                } else {
                    if (lessonNav.percentComplete > 0) {
                        courseNavItem.furtherestLesson = lesson;
                    }
                }

                previousLessonComplete = lessonNav.isComplete;

                moduleNav.lessons.push(lessonNav);
                moduleNav.totalLessons++;
                if (lessonNav.isComplete) {
                    moduleNav.completedLessons++;
                }
            }

            courseNavItem.modules.push(moduleNav);
            courseNavItem.totalLessons += moduleNav.totalLessons;
            courseNavItem.completedLessons += moduleNav.completedLessons;
        }

        courseNavItem.percentComplete = courseNavItem.totalLessons > 0
            ? Math.round((courseNavItem.completedLessons / courseNavItem.totalLessons) * 100)
            : 0;

        if (!courseNavItem.furtherestLesson) {
            courseNavItem.furtherestLesson = course.CourseModule[0]?.CourseLesson[0];
        }

        return courseNavItem
    }

    determineLessonIcon(lesson: CourseLessonEntity, lessonProgress: CourseLessonProgressEntity): string {
        let icon: string = 'fa fa-info-circle';

        switch (lesson.LessonTypeId) {
            case LessonTypeAllItems.Video:
                icon = 'fa fa-play-circle';
                break;
            case LessonTypeAllItems.Quiz:
                icon = 'fa fa-list-alt';
                break;
        }

        if (lessonProgress?.LessonComplete) {
            icon = 'fa fa-check-circle dw-green';
        } else {
            if (lessonProgress?.PercentComplete > 0 || lessonProgress?.LastActivityDateTime) {
                icon += ' dw-orange';
            }
            else {
                icon += ' normal-icon';
            }
        }

        return icon;
    }

    getLessonProgress(lessonReference: LessonReferenceInfo, studentCourse?: StudentCourseEntity): Observable<LessonProgressHierarchy> {

        return this.getCourseProgress(lessonReference.courseId, studentCourse)
            .pipe(map(courseProgress => {
                if (!courseProgress) {
                    return null;
                }

                const moduleProgress = courseProgress.modules?.find(m => m.module.CourseModuleId == lessonReference.moduleId);

                const lessonProgress: LessonProgressHierarchy = {
                    lessonReference: lessonReference,
                    course: courseProgress,
                    module: moduleProgress,
                    lesson: moduleProgress?.lessons.find(l => l.lesson.CourseLessonId == lessonReference.lessonId)
                };

                return lessonProgress;
            }));
    }

    getLessonDetails(lessonReference: LessonReferenceInfo): Observable<CourseLessonEntity> {
        if (!lessonReference?.courseId)
            return of(null);

        return this.getCourseHierarchy(lessonReference.courseId)
            .pipe(map(course => {
                if (!lessonReference.moduleId) {
                    return null;
                }

                const module = course.CourseModule.find(m => m.CourseModuleId == lessonReference.moduleId);

                if (!module) {
                    return null;
                }

                if (!lessonReference.lessonId) {
                    return null;
                }

                return module.CourseLesson.find(l => l.CourseLessonId == lessonReference.lessonId);
            }));
    }

    private loadCourseHierarchy(courseId: number): Observable<CourseEntity> {
        const query = AppMetaData.Course.CreateQueryBuilder();

        query.setFieldSettings(s => s.LoadAllLookupDisplayFields = true);
        query.addFilterAnd(f => f.Equal(a => a.CourseId, courseId));

        const moduleQuery = AppMetaData.CourseModule.CreateQueryBuilder();

        moduleQuery.addFilterAnd(f => f.Equal(a => a.ModuleStatusId, ReferenceStatusAllItems.Active));

        query.addPrefetch(r => r.CourseModule, moduleQuery.query);

        const lessonQuery = AppMetaData.CourseLesson.CreateQueryBuilder();

        const videoQuery = AppMetaData.Video.CreateQueryBuilder();

        videoQuery.addPrefetch(r => r.VideoLibrary); 
        videoQuery.addPrefetch(r => r.VideoStreamingProvider);
        
        lessonQuery.addPrefetch(r => r.Video, videoQuery.query);
        lessonQuery.addPrefetch(r => r.Quiz);
        lessonQuery.addPrefetch(r => r.DocumentItem);
        lessonQuery.addPrefetch(r => r.DwRoMediaByLessonImageMediaId)
        const lessonDocumentLinkQuery = AppMetaData.CourseLessonDocumentLink.CreateQueryBuilder();
        lessonDocumentLinkQuery.addFilterAnd(f => f.Equal(a => a.CourseLessonDocumentLinkStatusId, ReferenceStatusAllItems.Active));
        const lessonDocumentLinkReferenceQuery = AppMetaData.CourseLessonDocumentLinkReference.CreateQueryBuilder();
        lessonDocumentLinkReferenceQuery.addFilterAnd(f => f.Equal(a => a.ReferenceStatusId, ReferenceStatusAllItems.Active));
        lessonDocumentLinkReferenceQuery.addOrderBy(o => o.DisplayOrder);
        lessonDocumentLinkReferenceQuery.addPrefetch(r => r.DocumentSection);
        lessonDocumentLinkQuery.addPrefetch(r => r.CourseLessonDocumentLinkReference, lessonDocumentLinkReferenceQuery.query);
        lessonQuery.addPrefetch(r => r.CourseLessonDocumentLink, lessonDocumentLinkQuery.query);

        lessonQuery.addFilterAnd(f => f.Equal(a => a.LessonStatusId, ReferenceStatusAllItems.Active));

        moduleQuery.addPrefetch(r => r.CourseLesson, lessonQuery.query);

        moduleQuery.addOrderBy(o => o.ModuleOrder);
        lessonQuery.addOrderBy(o => o.LessonOrder);

        return this.dwOrmDataService.executeQuerySingle(query)
            .pipe(map(result => {

                // // force an error
                // const t = null;

                // t.test = 5;

                return result;
            }));
    }

    private loadCourseSummaries(): Observable<CourseSummary[]> {
        const query = AppMetaData.Course.CreateQueryBuilder();

        query.addFields(f => [f.CourseId, f.CourseName]);
        query.addOrderBy(o => o.CourseName);

        const moduleQuery = AppMetaData.CourseModule.CreateQueryBuilder();
        moduleQuery.addFields(f => [f.CourseModuleId, f.CourseId]);
        moduleQuery.addFilterAnd(f => f.Equal(a => a.ModuleStatusId, ReferenceStatusAllItems.Active));

        const lessonQuery = AppMetaData.CourseLesson.CreateQueryBuilder();
        lessonQuery.addFields(f => [f.CourseLessonId, f.CourseModuleId]);
        lessonQuery.addFilterAnd(f => f.Equal(a => a.LessonStatusId, ReferenceStatusAllItems.Active));

        moduleQuery.addPrefetch(r => r.CourseLesson, lessonQuery.query);
        query.addPrefetch(r => r.CourseModule, moduleQuery.query);

        return this.dwOrmDataService.executeQuery<CourseEntity>(query)
            .pipe(map(courses => (courses || []).map(course => {
                const modules = course.CourseModule || [];

                return {
                    courseId: course.CourseId,
                    courseName: course.CourseName,
                    moduleCount: modules.length,
                    lessonCount: modules.reduce((total, courseModule) => total + (courseModule.CourseLesson?.length || 0), 0)
                };
            })));
    }

    private loadCurrentStudentCourses(): Observable<StudentCourseEntity[]> {
        const query = AppMetaData.StudentCourse.CreateQueryBuilder();

        // Only logged in users have course progress
        const userId = this.dwSecurityUserService.securityContext?.ApplicationUser?.UserId;

        if (!userId) {
            return of(null);
        }

        query.addRelation(r => r.Student);

        query.addFilterAnd(f => f.Equal(a => DevwareMetaData.DwUser.UserId, userId));
        query.addFilterAnd(f => f.Equal(a => a.StudentCourseStatusId, ReferenceStatusAllItems.Active));
        //query.addFilterAnd(f => f.Equal(a => AppMetaData.Course.Attributes.CourseStatusId, ReferenceStatusAllItems.Active));

        query.addPrefetch(r => r.CourseEndorsementStudent);

        query.addOrderByDesc(o => o.LastActivityDateTime);

        query.query.SetFieldSettings(false, true);

        return this.dwOrmDataService.executeQuery(query)
            .pipe(catchError(errResponse => {
                console.error('Error loading student courses', errResponse);

                return of([]);
            }),)
    }

    private loadCourseForCourse(courseId: number): Observable<CourseEntity> {
        const query = AppMetaData.Course.CreateQueryBuilder();

        query.setFieldSettings(s => s.LoadAllLookupDisplayFields = false);
        query.addFilterAnd(f => f.Equal(a => a.CourseId, courseId));

        const moduleQuery = AppMetaData.CourseModule.CreateQueryBuilder();

        moduleQuery.addFilterAnd(f => f.Equal(a => a.ModuleStatusId, ReferenceStatusAllItems.Active));

        query.addPrefetch(r => r.CourseModule, moduleQuery.query);

        const lessonQuery = AppMetaData.CourseLesson.CreateQueryBuilder();


        moduleQuery.addPrefetch(r => r.CourseLesson, lessonQuery.query);

        moduleQuery.addOrderBy(o => o.ModuleOrder);
        lessonQuery.addOrderBy(o => o.LessonOrder);

        return this.dwOrmDataService.executeQuerySingle(query)
            .pipe(map(result => {

                // // force an error
                // const t = null;

                // t.test = 5;

                return result;
            }));
    }

    private loadStudentCourseWithProgress(courseId: number): Observable<StudentCourseEntity> {
        const query = AppMetaData.StudentCourse.CreateQueryBuilder();

        // Only logged in users have course progress
        const userId = this.dwSecurityUserService.securityContext?.ApplicationUser?.UserId;

        if (!userId) {
            return of(null);
        }

        query.addRelation(r => r.Student);

        query.addFilterAnd(f => f.Equal(a => DevwareMetaData.DwUser.UserId, userId));
        query.addFilterAnd(f => f.Equal(a => a.CourseId, courseId));
        query.addFilterAnd(f => f.Equal(a => a.StudentCourseStatusId, ReferenceStatusAllItems.Active));

        query.setFieldSettings(s => s.LoadAllLookupDisplayFields = true);

        // Add hierarchy of progress and session
        const moduleProgressQuery = AppMetaData.CourseModuleProgress.CreateQueryBuilder();
        const lessonProgressQuery = AppMetaData.CourseLessonProgress.CreateQueryBuilder();
        //const lessonSessionQuery = AppMetaData.CourseLessionSession.CreateQueryBuilder();

        query.addPrefetch(r => r.CourseEndorsementStudent);
        query.addPrefetch(r => r.CourseModuleProgress, moduleProgressQuery.query);

        moduleProgressQuery.addPrefetch(r => r.CourseLessonProgress, lessonProgressQuery.query);

        moduleProgressQuery.addPrefetch(r => r.AIPromptRequest,
            AppMetaData.AIPromptRequest.CreateQueryBuilder()
                .addField(f => f.AIPromptRequestStatusId)
            .query);

        // Pull the per-student module summary along with the rest of the
        // progress hierarchy so the module-detail "AI Summary" tab can render
        // without a follow-up request. Limit to the HTML body + status — the
        // full record carries audit fields we don't need here.
        moduleProgressQuery.addPrefetch(r => r.StudentModuleSummary,
            AppMetaData.StudentModuleSummary.CreateQueryBuilder()
                .addField(f => f.ModuleSummaryHtml)
                .addField(f => f.StudentModuleSummaryStatusId)
            .query);

        moduleProgressQuery.addFilterAnd(r => r.Equal(a => a.ModuleProgressStatusId, ReferenceStatusAllItems.Active));
        lessonProgressQuery.addFilterAnd(r => r.Equal(a => a.LessonProgressStatusId, ReferenceStatusAllItems.Active));

        return this.dwOrmDataService.executeQuerySingle(query)
            .pipe(map(studentCourse => {
                return studentCourse;
            }));
    }

    private loadStudentCourseWithProgressEnsured(courseId: number): Observable<StudentCourseEntity> {
        const url = `${this.studentProgressApi}/current-student-course-hierarchy?courseId=${courseId}`;

        return this.http.get<StudentCourseEntity>(url);
    }

     loadStudentCourseWithProgressAdmin(courseId: number, studentId: number): Observable<StudentCourseEntity> {
        const query = AppMetaData.StudentCourse.CreateQueryBuilder();

        
        query.addFilterAnd(f => f.Equal(a => a.StudentId, studentId));
        query.addFilterAnd(f => f.Equal(a => a.CourseId, courseId));
        query.addFilterAnd(f => f.Equal(a => a.StudentCourseStatusId, ReferenceStatusAllItems.Active));

        query.setFieldSettings(s => s.LoadAllLookupDisplayFields = true);

        // Add hierarchy of progress and session
        const moduleProgressQuery = AppMetaData.CourseModuleProgress.CreateQueryBuilder();
        const lessonProgressQuery = AppMetaData.CourseLessonProgress.CreateQueryBuilder();
        //const lessonSessionQuery = AppMetaData.CourseLessionSession.CreateQueryBuilder();

       // query.addPrefetch(r => r.CourseEndorsementStudent);
        query.addPrefetch(r => r.CourseModuleProgress, moduleProgressQuery.query);

        moduleProgressQuery.addPrefetch(r => r.CourseLessonProgress, lessonProgressQuery.query);
        
        // moduleProgressQuery.addPrefetch(r => r.AIPromptRequest, 
        //     AppMetaData.AIPromptRequest.CreateQueryBuilder()
        //         .addField(f => f.AIPromptRequestStatusId)
        //     .query);

        lessonProgressQuery.addPrefetch(r => r.QuizResult);

        moduleProgressQuery.addFilterAnd(r => r.Equal(a => a.ModuleProgressStatusId, ReferenceStatusAllItems.Active));
        lessonProgressQuery.addFilterAnd(r => r.Equal(a => a.LessonProgressStatusId, ReferenceStatusAllItems.Active));

        return this.dwOrmDataService.executeQuerySingle(query)
            .pipe(map(studentCourse => {
                return studentCourse;
            }));
    }

    loadAssignmentStudentLessonProgress(assignmentId: number): Observable<RoAssignmentStudentCourseLessonProgressEntity[]> {
        const query = AppMetaData.RoAssignmentStudentCourseLessonProgress.CreateQueryBuilder();

        if (!assignmentId) {
            return of([]);
        }

        query.query.FieldSettings.DistinctRowsOnly = true;
        query.addFilterAnd(f => f.Equal(a => a.AssignmentId, assignmentId));
        query.addOrderBy(f => f.ModuleOrder);
        query.addOrderBy(f => f.LessonOrder);
        query.addOrderBy(f => f.AssignmentStudentId);

        return this.dwOrmDataService.executeQuery(query);
    }

    /**
     * 
     * @param progress 
     * @param lessonProgress 
     * @returns 
     * @remarks 
     *   // update module progress (if lesson progress has changed)
     *   // Update student course
     *   // Save changes
     *   // update cache
     *   // publish refresh event
     *   // return updated progress object
     */
    updateLessonProgress(progress: LessonProgressHierarchy, lessonProgress: Partial<CourseLessonProgressEntity>, sessionTimeToAddSeconds: number, forceReload: boolean = false): Observable<LessonProgressHierarchy> {
        this.ensureProgressHierarchy(progress, progress.currentLessonSession.SessionStartDateTime);

        const lessonProgressToSave: CourseLessonProgressEntity = {
            ...progress.lesson.lessonProgress,
            ...lessonProgress,
            CourseLessonSession: []
        };
        delete lessonProgressToSave.CompletedQuizAttemptCount;
        delete lessonProgressToSave.PassedQuizAttemptCount;

        const currentSession: CourseLessonSessionEntity = {
            _itemName: AppMetaDataItemNames.CourseLessonSession,
            CourseLessonProgressId: lessonProgressToSave.CourseLessonProgressId,
            ...progress.currentLessonSession
        };

        // We only want to pass the student course, not all the module and lesson details
        const studentCourseOnly = {...progress.course.studentCourse, CourseModuleProgress: []};
        const courseModuleProgressOnly = {...progress.module.moduleProgress, CourseLessonProgress: []};

        var request: StudentUpdateLessonProgressRequest = {
            SessionTimeToAddSeconds: sessionTimeToAddSeconds,
            StudentCourse: studentCourseOnly,
            CourseModuleProgress: courseModuleProgressOnly,
            CourseLessonProgress: lessonProgressToSave,
            CourseLessonSession: currentSession,
        }

        return this.http.post<StudentUpdateLessonProgressResult>(`${this.studentProgressApi}/update-lesson-progress`, request)
            .pipe(mergeMap(result => {
                if (!result.Success) {
                    console.warn('Error updating lesson progress', result.Message);

                    return of(progress);
                }

                if (result.RequiresFullReload || forceReload) {
                    return this.loadStudentCourseWithProgressEnsured(progress.lessonReference.courseId)
                        .pipe(
                            mergeMap(studentCourse => this.getLessonProgress(progress.lessonReference, studentCourse)),
                            mergeMap(updatedProgress => {

                                this.updateStudentCourseCache(updatedProgress.course.studentCourse, true);

                                if (result.Endorsed) {
                                    this.handleCourseEndorsement(result.StudentCourse);
                                }

                                updatedProgress.currentLessonSession = result.CourseLessonSession;

                                return of(updatedProgress);
                            })
                        );
                }

                return of(this.finalizeLessonProgressUpdate(progress, result));
            }), tap(() => this.gamificationRepositoryService.invalidate()), catchError(error => {
                console.warn('Error updating lesson progress', {
                    error,
                    studentCourseId: progress?.course?.studentCourse?.StudentCourseId,
                    courseModuleId: progress?.module?.module?.CourseModuleId,
                    courseLessonId: progress?.lesson?.lesson?.CourseLessonId
                });

                return of(progress);
            }));
    }

    updateLessonProgressOld(progress: LessonProgressHierarchy, lessonProgress: Partial<CourseLessonProgressEntity>, sessionTimeToAddSeconds: number): Observable<LessonProgressHierarchy> {
        this.ensureProgressHierarchy(progress, progress.currentLessonSession.SessionStartDateTime);

        const lessonProgressToSave: CourseLessonProgressEntity = { ...progress.lesson.lessonProgress, ...lessonProgress };
        delete lessonProgressToSave.CompletedQuizAttemptCount;
        delete lessonProgressToSave.PassedQuizAttemptCount;

        // Check if there are any changes
        const courseModuleProgress = this.updataCourseModuleProgressOld(lessonProgressToSave, progress, sessionTimeToAddSeconds);

        if (progress.currentLessonSession) {
            // Save the lesson session
            const currentSession: CourseLessonSessionEntity = {
                _itemName: AppMetaDataItemNames.CourseLessonSession,
                CourseLessonProgressId: lessonProgressToSave.CourseLessonProgressId,
                ...progress.currentLessonSession
            };

            lessonProgressToSave.CourseLessonSession = [currentSession];
        }

        return this.dwOrmDataService.saveEntity(courseModuleProgress, true)
            .pipe(map(savedCourseModuleProgress => {

                if (!progress.course.studentCourse.IsEndorsed) {
                    this.checkCourseEndoresment(progress.course.studentCourse.StudentCourseId);
                }

                return this.finalizeLessonProgressUpdateOld(progress, savedCourseModuleProgress);
            }));
    }


    private checkCourseEndoresment(studentCourseId: number) {
        const studentCourse = AppMetaData.StudentCourse.CreateQueryBuilder();

        studentCourse.addFilterAnd(f => f.Equal(a => a.StudentCourseId, studentCourseId));

        studentCourse.query.FieldSettings.LoadAllLookupDisplayFields = true;
        studentCourse.addField(f => f.IsEndorsed);
        studentCourse.addField(f => f.CourseId);

        this.dwOrmDataService.executeQuerySingle(studentCourse)
            .subscribe(studentCourse => {
                if (studentCourse.IsEndorsed) {
                    this.handleCourseEndorsement(studentCourse);

                    // this.publishReloadStudentCourse(studentCourse);

                    // setTimeout(() => {
                    //     // const url = `/my-courses/view/${studentCourse.CourseId}`;
                    //     const url = `/my-courses?tile=endorsed`;

                    //     this.router.navigateByUrl(url);
                    // }, 500);

                    // this.dwModalService.showDialog('Congratulations!', 'You have completed the course and are now endorsed!  <br><br> Click the download endorsement button below to download your certificate!', [DwModalButtonStandardConfigs.instance.okButton])
                    //     .subscribe(() => {

                    //     });
                }
            });
    }

    private handleCourseEndorsement(studentCourse: StudentCourseEntity) {
        this.publishReloadStudentCourse(studentCourse);

        setTimeout(() => {
            // const url = `/my-courses/view/${studentCourse.CourseId}`;
            const url = `/my-courses?tile=endorsed`;

            this.router.navigateByUrl(url);
        }, 500);

        this.dwModalService.showDialog('Congratulations!', 'You have completed the course and are now endorsed!  <br><br> View the Course to download your endorsement certificate!', [DwModalButtonStandardConfigs.instance.okButton])
            .subscribe(() => {

            });
    }

    private finalizeLessonProgressUpdate(progress: LessonProgressHierarchy, updateResult: StudentUpdateLessonProgressResult): LessonProgressHierarchy {
        // Save module will always save the course lessage progress as well since that is what triggered the save
        const updatedLessonProgress = updateResult.CourseLessonProgress;

        const previousLessonProgress = progress.module.moduleProgress?.CourseLessonProgress
            ?.filter(p => !this.isSameLessonProgress(p, updateResult.CourseLessonProgress)) || [];

        const updatedCourseModuleProgress = updateResult.CourseModuleProgress;

        // Merge new lesson progress with existing lesson progress
        updatedCourseModuleProgress.CourseLessonProgress = [updatedLessonProgress, ...previousLessonProgress]

        progress.currentLessonSession = updateResult.CourseLessonSession;

        progress.lesson.lessonProgress = updatedLessonProgress;
        progress.module.moduleProgress = updatedCourseModuleProgress;

        // Check if the student course needs to be updated
        if (updateResult.StudentCourse) {
            const updatedStudentCourse = updateResult.StudentCourse;

            const previousStudentCourseModuleProgress = progress.course.studentCourse.CourseModuleProgress?.filter(p => p.CourseModuleProgressId != updatedCourseModuleProgress.CourseModuleProgressId) || [];

            updatedStudentCourse.CourseModuleProgress = [updatedCourseModuleProgress, ...previousStudentCourseModuleProgress];

            progress.course.studentCourse = updatedStudentCourse;
        }

        console.log('Update student course cache');

        // Need to update the project cache
        this.updateStudentCourseCache(progress.course.studentCourse);

        return progress;
    }

    private finalizeLessonProgressUpdateOld(progress: LessonProgressHierarchy, updatedCourseModuleProgress: CourseModuleProgressEntity): LessonProgressHierarchy {
        // Save module will always save the course lessage progress as well since that is what triggered the save
        const updatedCourseProgress = updatedCourseModuleProgress.CourseLessonProgress?.[0];


        const previousLessonProgress = progress.module.moduleProgress?.CourseLessonProgress
            ?.filter(p => !this.isSameLessonProgress(p, updatedCourseProgress)) || [];

        // Merge new lesson progress with existing lesson progress
        updatedCourseModuleProgress.CourseLessonProgress = [updatedCourseProgress, ...previousLessonProgress]


        progress.currentLessonSession = updatedCourseProgress.CourseLessonSession?.[0];

        updatedCourseProgress.CourseLessonSession = null;

        progress.lesson.lessonProgress = updatedCourseProgress;
        progress.module.moduleProgress = updatedCourseModuleProgress;

        // Check if the student course needs to be updated
        if (updatedCourseModuleProgress.StudentCourse) {
            const updatedStudentCourse = updatedCourseModuleProgress.StudentCourse;

            const previousStudentCourseModuleProgress = progress.course.studentCourse.CourseModuleProgress?.filter(p => p.CourseModuleProgressId != updatedCourseModuleProgress.CourseModuleProgressId) || [];

            updatedStudentCourse.CourseModuleProgress = [updatedCourseModuleProgress, ...previousStudentCourseModuleProgress];

            progress.course.studentCourse = updatedStudentCourse;
        }

        // Need to update the project cache
        this.updateStudentCourseCache(progress.course.studentCourse);

        return progress;
    }


    // private updateLessonProgress(request: StudentUpdateLessonProgressRequest): Observable<StudentUpdateLessonProgressResult> {
    //     return this.http.post<StudentUpdateLessonProgressResult>(`${this.studentProgressApi}/update-lesson-progress`, request)
    // }

    /**
     * Updates the module and course completed info
     * @param lessonProgress 
     * @param progress 
     * @returns Returns either the lesson or module to be saved
     */
    private updataCourseModuleProgressOld(lessonProgress: CourseLessonProgressEntity, progress: LessonProgressHierarchy, sessionTimeToAddSeconds: number): CourseModuleProgressEntity {
        const moduleProgress = { ...progress.module.moduleProgress };

        moduleProgress.LessonsCompleted = progress.module.lessons.filter(l => l.lessonProgress?.LessonComplete && l.lesson.CourseLessonId != lessonProgress.CourseLessonId).length;

        var currentSession = progress.currentLessonSession;

        if (!lessonProgress.FirstActivityDateTime) {
            lessonProgress.FirstActivityDateTime = currentSession.SessionStartDateTime;
        }

        lessonProgress.LastActivityDateTime = currentSession.SessionEndDateTime;

        // Increase the lesson count if the lesson is complete
        if (lessonProgress.LessonComplete) {
            lessonProgress.PercentComplete = 100;
            if (!lessonProgress.CompletedDateTime) {
                lessonProgress.CompletedDateTime = lessonProgress.LastActivityDateTime;
            }

            moduleProgress.LessonsCompleted++;
        }

        lessonProgress.TotalTimeSeconds = (lessonProgress.TotalTimeSeconds || 0) + sessionTimeToAddSeconds;  // Add newest session time

        moduleProgress.LessonsCount = progress.module.lessons.length;
        moduleProgress.PercentComplete = moduleProgress.LessonsCount > 0 ? (moduleProgress.LessonsCompleted / moduleProgress.LessonsCount) * 100 : 0;
        moduleProgress.LastActivityDateTime = lessonProgress.LastActivityDateTime;
        moduleProgress.TotalTimeSeconds = this.sum(moduleProgress.CourseLessonProgress
            .filter(c => c.CourseLessonProgressId != lessonProgress.CourseLessonProgressId), l => l.TotalTimeSeconds) // Remove current lesson
            + lessonProgress.TotalTimeSeconds;   // Add newest lesson time

        if (!moduleProgress.FirstActivityDateTime) {
            moduleProgress.FirstActivityDateTime = lessonProgress.FirstActivityDateTime;
        }

        if (moduleProgress.PercentComplete == 100 && !moduleProgress.CompletedDateTime) {
            moduleProgress.CompletedDateTime = lessonProgress.CompletedDateTime;
        }

        // Add the module progress to the lesson progress
        moduleProgress.CourseLessonProgress = [lessonProgress];

        const studentCourse = { ...progress.course.studentCourse, CourseModuleProgress: [] };


        let modulesCompleted = progress.course.modules.filter(m => m.moduleProgress?.PercentComplete == 100 && m.module.CourseModuleId != moduleProgress.CourseModuleId).length;

        if (moduleProgress.PercentComplete == 100) {
            modulesCompleted++;
        }

        studentCourse.ModulesCompleted = modulesCompleted;

        studentCourse.ModulesCount = progress.course.modules.length;
        let otherLessonsCompleted = this.sum(progress.course.modules.filter(m => m.module.CourseModuleId != moduleProgress.CourseModuleId), m => m.moduleProgress?.LessonsCompleted);

        studentCourse.LessonsCompleted = otherLessonsCompleted + moduleProgress.LessonsCompleted;

        studentCourse.LessonCount = this.sum(progress.course.modules, m => m.lessons.length);
        studentCourse.PercentComplete = studentCourse.LessonCount > 0 ? (studentCourse.LessonsCompleted / studentCourse.LessonCount) * 100 : 0;
        studentCourse.LastActivityDateTime = lessonProgress.LastActivityDateTime;

        if (!studentCourse.FirstActivityDateTime) {
            studentCourse.FirstActivityDateTime = moduleProgress.FirstActivityDateTime;
        }
        if (studentCourse.PercentComplete == 100 && !studentCourse.CompletedDateTime) {
            studentCourse.CompletedDateTime = moduleProgress.CompletedDateTime || lessonProgress.CompletedDateTime;
        }

        studentCourse.TotalVideoTimeSeconds = this.sum(progress.course.modules.filter(m => m.moduleProgress && m.moduleProgress?.CourseModuleId != moduleProgress.CourseModuleId)
            , m => m.moduleProgress.TotalVideoTimeSeconds) + moduleProgress.TotalVideoTimeSeconds;
        studentCourse.TotalQuizTimeSeconds = this.sum(progress.course.modules.filter(m => m.moduleProgress && m.moduleProgress?.CourseModuleId != moduleProgress.CourseModuleId)
            , m => m.moduleProgress.TotalVideoTimeSeconds) + moduleProgress.TotalQuizTimeSeconds;

        studentCourse.TotalTimeSeconds = this.sum(progress.course.modules.filter(m => m.moduleProgress && m.moduleProgress?.CourseModuleId != moduleProgress.CourseModuleId)
            , m => m.moduleProgress.TotalTimeSeconds) + moduleProgress.TotalTimeSeconds;

        // Add the student course to save
        moduleProgress.StudentCourse = studentCourse;

        // We just need to save module Progress
        return moduleProgress;
    }

    private sum<T>(items: T[], selector: (item: T) => number): number {
        return items.reduce((p, c) => p + selector(c) || 0, 0);
    }

    assignCourseToAllSchools(courseId: number): Observable<number> {
        return this.http.post<number>(`${this.courseApi}/${courseId}/assign-all-schools`, null);
    }

    getCourseAvailabilityNotifications(): Observable<CourseAvailabilityNotificationEntity[]> {
        const aviatorUser = this.adminManagerService.getAviatorUser();
        const cacheKey = `CourseAvailabilityNotifications-${aviatorUser.user?.UserId}-${aviatorUser.FlightSchoolId}`;
        const invalidationTopics = [
            DwSecurityTopics.SECURITY_CHANGED,
            DwOrmDataService.ORM_EVENT_TOPICS.ItemChangedTopic(AppMetaDataItemNames.CourseAvailabilityNotification)
        ];

        return this.dwCacheService.get(cacheKey, this.loadCourseAvailabilityNotifications(), null, invalidationTopics);
    }

    setCourseAvailabilityNotification(courseId: number, isActive: boolean): Observable<CourseAvailabilityNotificationEntity> {
        return this.getCourseAvailabilityNotifications()
            .pipe(mergeMap(notifications => {
                const aviatorUser = this.adminManagerService.getAviatorUser();
                const notification = notifications.find(item => item.CourseId === courseId)
                    || AppMetaData.CourseAvailabilityNotification.CreateEntity();
                const now = new Date().toISOString();

                notification.CourseId = courseId;
                notification.UserId = aviatorUser.user?.UserId;
                notification.FlightSchoolId = aviatorUser.FlightSchoolId;
                notification.IsActive = isActive;
                notification.RequestedDateTime = isActive ? now : notification.RequestedDateTime;
                notification.CancelledDateTime = isActive ? null : now;
                notification.CourseAvailableDateTime = isActive ? null : notification.CourseAvailableDateTime;

                return this.dwOrmDataService.saveEntity(notification, true);
            }));
    }

    private loadCourseAvailabilityNotifications(): Observable<CourseAvailabilityNotificationEntity[]> {
        const aviatorUser = this.adminManagerService.getAviatorUser();
        const query = AppMetaData.CourseAvailabilityNotification.CreateQueryBuilder();

        query.addFilterAnd(f => f.Equal(a => a.UserId, aviatorUser.user?.UserId));
        query.addFilterAnd(f => f.Equal(a => a.FlightSchoolId, aviatorUser.FlightSchoolId));

        return this.dwOrmDataService.executeQuery(query);
    }

    private isSameLessonProgress(current: CourseLessonProgressEntity, updated: CourseLessonProgressEntity): boolean {
        if (!current || !updated) {
            return false;
        }

        if (current.CourseLessonProgressId && updated.CourseLessonProgressId) {
            return current.CourseLessonProgressId === updated.CourseLessonProgressId;
        }

        return current.CourseLessonId === updated.CourseLessonId;
    }

    /**
     * Ensure the module progress and lesson progress entities are created
     * @param progress 
     */
    private ensureProgressHierarchy(progress: LessonProgressHierarchy, startDateTime: any) {
        if (!progress.module.moduleProgress) {
            // Create module progress
            progress.module.moduleProgress = {
                _itemName: AppMetaDataItemNames.CourseModuleProgress,
                CourseModuleId: progress.module.module.CourseModuleId,
                PercentComplete: 0,
                LessonsCompleted: 0,
                LessonsCount: progress.module.lessons.length,
                StudentCourseId: progress.course.studentCourse.StudentCourseId,
                FirstActivityDateTime: startDateTime
            };
        }

        if (!progress.lesson.lessonProgress) {

            // Create lesson progress
            progress.lesson.lessonProgress = {
                _itemName: AppMetaDataItemNames.CourseLessonProgress,
                CourseLessonId: progress.lesson.lesson.CourseLessonId,
                LessonComplete: false,
                PercentComplete: 0,
                VideoFurthestTimeSeconds: 0,
                CourseModuleProgressId: progress.module.moduleProgress.CourseModuleProgressId, // populate FK if it exists
                FirstActivityDateTime: startDateTime
            };
        }


    }

}
